import type { EscapeRoom } from '@/domain/escapeRoom'

// P4 seed content for the Escape Room Engine (Document 11 §P4). Every piece
// of "evidence" below is simulated text authored as data — no real emails,
// logs, credentials, or systems are involved (Escape Room Safety
// requirement). The room is fully data-driven so a second room is an
// addition to this array, not a new component.
export const seedEscapeRooms: EscapeRoom[] = [
  {
    id: 'room-breach-at-demo-enterprise',
    title: 'Breach at Demo Enterprise',
    briefing:
      "Demo Enterprise's systems show signs of compromise. You have 10 minutes to trace the attack, contain it, and recover access. Work through the evidence in order — each challenge unlocks the next.",
    timeLimitSeconds: 600,
    challenges: [
      {
        id: 'challenge-1',
        order: 1,
        title: 'Identify the phishing email',
        prompt: 'Three messages arrived in the IT inbox this morning. Which one is the phishing attempt?',
        evidence: [
          {
            id: 'email-1',
            label: 'Email 1 — from: it-support@demoenterprise.example',
            content:
              'Subject: Scheduled maintenance tonight\n\nHi team, reminder that the file server will be offline for maintenance from 11pm–1am. No action needed.',
          },
          {
            id: 'email-2',
            label: 'Email 2 — from: security-alert@demo-enterprise-verify.com',
            content:
              'Subject: URGENT — Unusual sign-in detected\n\nWe detected a sign-in from an unrecognized device. Verify your identity immediately or your account will be locked in 30 minutes: [Verify Now]',
          },
          {
            id: 'email-3',
            label: 'Email 3 — from: hr@demoenterprise.example',
            content:
              'Subject: Updated holiday calendar\n\nThe updated public holiday calendar for this year is attached for your records.',
          },
        ],
        type: 'multiple_choice',
        options: [
          { id: 'e1', text: 'Email 1 — Scheduled maintenance tonight' },
          { id: 'e2', text: 'Email 2 — Unusual sign-in detected' },
          { id: 'e3', text: 'Email 3 — Updated holiday calendar' },
        ],
        correctOptionId: 'e2',
        hint: 'Look closely at the sender domain, not just the display name.',
        explanation:
          'Email 2 is from "demo-enterprise-verify.com" — not the real "demoenterprise.example" domain — and uses urgency ("30 minutes") to rush a click. Classic phishing pattern.',
      },
      {
        id: 'challenge-2',
        order: 2,
        title: 'Find the suspicious login',
        prompt: 'Here is the login activity log for the affected account. Which entry looks suspicious?',
        evidence: [
          {
            id: 'log-1',
            label: 'System Log — Authentication',
            content:
              '08:58:02  user=kwame.employee  ip=41.203.12.9   location=Accra, GH        result=SUCCESS\n09:14:47  user=kwame.employee  ip=41.203.12.9   location=Accra, GH        result=SUCCESS\n09:16:03  user=kwame.employee  ip=185.220.101.44 location=Unknown (Tor exit) result=SUCCESS\n09:16:55  user=kwame.employee  ip=185.220.101.44 location=Unknown (Tor exit) result=PASSWORD_CHANGED',
          },
        ],
        type: 'multiple_choice',
        options: [
          { id: 'l1', text: '08:58:02 — sign-in from Accra, GH' },
          { id: 'l2', text: '09:14:47 — sign-in from Accra, GH' },
          { id: 'l3', text: '09:16:03 — sign-in from an unknown Tor exit node' },
        ],
        correctOptionId: 'l3',
        hint: 'The first two entries are from the same normal location the account always signs in from. What changed on the third line?',
        explanation:
          'A sign-in from an anonymized Tor exit node, immediately followed by a password change, is a strong sign the account was taken over right after the phishing link was clicked.',
      },
      {
        id: 'challenge-3',
        order: 3,
        title: 'Identify the compromised credential',
        prompt: 'Based on the log, whose account was compromised? Type the username exactly as it appears in the log.',
        evidence: [
          {
            id: 'log-1-repeat',
            label: 'System Log — Authentication (same as before)',
            content:
              '09:16:03  user=kwame.employee  ip=185.220.101.44 location=Unknown (Tor exit) result=SUCCESS\n09:16:55  user=kwame.employee  ip=185.220.101.44 location=Unknown (Tor exit) result=PASSWORD_CHANGED',
          },
        ],
        type: 'text_input',
        acceptedAnswers: ['kwame.employee'],
        hint: "It's the username on the suspicious log lines from Challenge 2.",
        explanation: "kwame.employee's credentials were the ones used in the unauthorized sign-in and password change.",
      },
      {
        id: 'challenge-4',
        order: 4,
        title: 'Choose the correct containment action',
        prompt: "kwame.employee's account is actively compromised right now. What's the correct first containment step?",
        evidence: [
          {
            id: 'note-1',
            label: 'IT Response Playbook (excerpt)',
            content:
              'For a confirmed account takeover: disable/suspend the account immediately, then force a password reset and re-enable MFA before restoring access. Do not wait for the user to notice.',
          },
        ],
        type: 'multiple_choice',
        options: [
          { id: 'c1', text: 'Wait and see if anything else unusual happens' },
          { id: 'c2', text: "Immediately suspend the account, then reset the password and enforce MFA" },
          { id: 'c3', text: 'Email the attacker asking them to stop' },
          { id: 'c4', text: 'Shut down the entire company network' },
        ],
        correctOptionId: 'c2',
        hint: 'Check the IT Response Playbook evidence for the documented procedure.',
        explanation:
          'Suspending the compromised account first stops further damage immediately, without the disruption of a full network shutdown. Password reset + MFA follows before access is restored.',
      },
      {
        id: 'challenge-5',
        order: 5,
        title: 'Find the final clue',
        prompt: 'With the account contained, find the recovery verification code left in the security team\'s incident notes.',
        evidence: [
          {
            id: 'note-2',
            label: 'Incident Notes — Security Team',
            content:
              'Account suspended at 09:20. Password reset pending. Recovery verification code for the ticketing system: CT-7734-SAFE\n(Do not share this code outside the response team.)',
          },
        ],
        type: 'text_input',
        acceptedAnswers: ['ct-7734-safe', 'ct-7734safe', '7734'],
        hint: 'It\'s written directly in the Incident Notes evidence, formatted like a ticket code.',
        explanation: 'The verification code CT-7734-SAFE confirms the incident was documented and handled by the response team.',
      },
      {
        id: 'challenge-6',
        order: 6,
        title: 'Unlock the system',
        prompt: 'Enter the verification code you found to close out the incident and restore normal access.',
        evidence: [
          {
            id: 'note-3',
            label: 'System — Recovery Console',
            content: 'Enter the incident verification code to mark this breach as resolved and restore account access.',
          },
        ],
        type: 'text_input',
        acceptedAnswers: ['ct-7734-safe', 'ct-7734safe'],
        hint: 'Reuse the code from Challenge 5.',
        explanation: "Incident resolved — kwame.employee's account is restored with a new password and MFA enabled.",
      },
    ],
  },
]
